import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { TezosToolkit } from '@taquito/taquito';
import { BeaconWallet } from '@taquito/beacon-wallet';

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export const WalletProvider = ({ children }) => {
  const [wallet, setWallet] = useState(null);
  const [tezos, setTezos] = useState(null);
  const [userAddress, setUserAddress] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [balance, setBalance] = useState(null);

  // Memoized balance updater (placed before effects to avoid TDZ)
  const updateBalance = useCallback(async (address) => {
    if (tezos && address) {
      try {
        const balance = await tezos.tz.getBalance(address);
        setBalance(balance.toNumber() / 1000000); // Convert from mutez to tez
        console.log('Balance updated:', balance.toNumber() / 1000000, 'tez');
      } catch (error) {
        console.error('Error fetching balance:', error);
      }
    }
  }, [tezos]);

  // Initialize Tezos toolkit with a single reliable endpoint
  useEffect(() => {
    const initTezos = () => {
      try {
        // Use a single, reliable RPC endpoint
        const tezosToolkit = new TezosToolkit('https://mainnet.api.tez.ie');
        setTezos(tezosToolkit);
        console.log('Tezos toolkit initialized successfully');
      } catch (error) {
        console.error('Error initializing Tezos toolkit:', error);
      }
    };

    initTezos();
  }, []);

  // Check for existing wallet connection
  useEffect(() => {
    const checkWalletConnection = async () => {
      if (tezos && wallet) {
        try {
          const activeAccount = await wallet.client.getActiveAccount();
          if (activeAccount) {
            setUserAddress(activeAccount.address);
            setIsConnected(true);
            await updateBalance(activeAccount.address);
            console.log('Found existing wallet connection:', activeAccount.address);
          }
        } catch (error) {
          console.error('Error checking wallet connection:', error);
        }
      }
    };

    checkWalletConnection();
  }, [tezos, wallet, updateBalance]);

  const connectWallet = async () => {
    if (!tezos) {
      console.error('Tezos toolkit not initialized');
      alert('Wallet system not ready. Please refresh the page and try again.');
      return;
    }

    setIsConnecting(true);
    try {
      console.log('Initializing Beacon wallet...');
      
      // Create Beacon wallet with network specified in constructor
      const beaconWallet = new BeaconWallet({
        name: 'NFT-People',
        preferredNetwork: 'mainnet',
        disableMetrics: true, // Disable metrics to avoid IndexedDB errors
      });

      console.log('Setting wallet provider...');
      tezos.setWalletProvider(beaconWallet);
      setWallet(beaconWallet);

      console.log('Requesting permissions...');
      // Remove the network property from requestPermissions - it's now set in constructor
      try {
        await beaconWallet.requestPermissions();
      } catch (permissionError) {
        // Suppress IndexedDB metrics errors that don't affect wallet connection
        if (permissionError.message && permissionError.message.includes('metrics not found')) {
          console.warn('⚠️ Beacon metrics error (non-critical):', permissionError.message);
          // Continue with wallet connection despite metrics error
        } else {
          // Re-throw other permission errors
          throw permissionError;
        }
      }

      console.log('Getting user address...');
      const userAddress = await beaconWallet.getPKH();
      setUserAddress(userAddress);
      setIsConnected(true);
      await updateBalance(userAddress);

      console.log('Wallet connected successfully:', userAddress);
    } catch (error) {
      console.error('Error connecting wallet:', error);
      
      // Provide specific error messages
      let errorMessage = 'Failed to connect wallet. Please try again.';
      
      if (error.name === 'NotGrantedBeaconError') {
        errorMessage = 'Wallet connection was rejected. Please try again and approve the connection.';
      } else if (error.name === 'NoBeaconError' || error.message.includes('NoBeaconError')) {
        errorMessage = 'No Tezos wallet found. Please install Temple Wallet and make sure it\'s unlocked.';
      } else if (error.message.includes('permission')) {
        errorMessage = 'Permission denied. Please approve the wallet connection request.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Connection timeout. Please try again.';
      } else if (error.message.includes('network')) {
        errorMessage = 'Network configuration error. Please refresh the page and try again.';
      }
      
      alert(errorMessage);
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWallet = async () => {
    if (wallet) {
      try {
        await wallet.clearActiveAccount();
        setWallet(null);
        setUserAddress(null);
        setIsConnected(false);
        setBalance(null);
        console.log('Wallet disconnected successfully');
      } catch (error) {
        console.error('Error disconnecting wallet:', error);
      }
    }
  };

  const purchaseNFT = async (tokenId, price, sellerAddress, marketplace = 'hen', swapId = null, askId = null) => {
    if (!tezos || !wallet || !isConnected) {
      alert('Please connect your wallet first');
      return;
    }

    try {
      let contractAddress, entrypoint, params;
      
      if (marketplace === 'hen' || marketplace === 'teia') {
        // Route HEN/Teia listings to Teia marketplace
        contractAddress = 'KT1PHubm9HtyQEJ4BBpMTVomq6mhbfNZ9z5w';
        entrypoint = 'collect';
        
        if (!swapId) {
          throw new Error(`Missing swap_id for HEN/Teia marketplace. NFT ${tokenId} needs a valid swap_id to purchase.`);
        }
        
        params = parseInt(swapId);
        
        console.log(`🔍 HEN/Teia MODE: Purchasing NFT #${tokenId} for ${price} tez`);
        console.log(`🔍 Contract: ${contractAddress} (Teia marketplace)`);
        console.log(`🔍 Entrypoint: collect(${swapId})`);
        console.log(`🔍 Swap ID: ${swapId}`);
        console.log(`🔍 Price in tez: ${price}`);
        
      } else if (marketplace === 'objkt') {
        // Route OBJKT listings to OBJKT marketplace
        contractAddress = 'KT1WvzYHCNBvDSdwafTHv7nJ1dWmZ8GCYuuC';
        entrypoint = 'fulfill_ask';
        
        if (!askId) {
          throw new Error(`Missing ask_id for OBJKT marketplace. NFT ${tokenId} needs a valid ask_id to purchase.`);
        }
        
        params = [parseInt(askId), null]; // ask_id, referrer (null for no referral)
        
        console.log(`🔍 OBJKT MODE: Purchasing NFT #${tokenId} for ${price} tez`);
        console.log(`🔍 Contract: ${contractAddress} (OBJKT marketplace)`);
        console.log(`🔍 Entrypoint: fulfill_ask(${askId}, null)`);
        console.log(`🔍 Ask ID: ${askId}`);
        console.log(`🔍 Price in tez: ${price}`);
        
      } else {
        throw new Error(`Unsupported marketplace: ${marketplace}. Supported: 'hen', 'teia', 'objkt'`);
      }
      
      const contract = await tezos.wallet.at(contractAddress);
      console.log('🔍 Contract loaded successfully');
      
      // Call the appropriate entrypoint
      let operation;
      if (entrypoint === 'collect') {
        operation = await contract.methods
          .collect(params)
          .send({ amount: price });
      } else if (entrypoint === 'fulfill_ask') {
        operation = await contract.methods
          .fulfill_ask(params[0], params[1])
          .send({ amount: price });
      }

      console.log('🔍 Purchase transaction sent:', operation.opHash);
      
      // Wait for confirmation
      await operation.confirmation();
      
      alert(`✅ Successfully purchased NFT #${tokenId}! Transaction: ${operation.opHash}`);
      
      // Update balance after purchase
      await updateBalance(userAddress);
      
      return operation.opHash;
    } catch (error) {
      console.error('❌ Error purchasing NFT:', error);
      
      // Provide detailed error information
      const errorMessage = error?.message || error?.toString() || 'Unknown error occurred';
      let userMessage = `Failed to purchase NFT: ${errorMessage}`;
      
      if (typeof errorMessage === 'string') {
        if (errorMessage.includes('MP_WRONG_SWAP_ID')) {
          userMessage = `❌ Wrong swap ID. The swap_id (${swapId}) is not valid on Teia marketplace.\n\nPlease check if this NFT is still available for purchase.`;
        } else if (errorMessage.includes('Missing swap_id')) {
          userMessage = `❌ ${errorMessage}\n\nThis NFT needs marketplace data to be purchasable.`;
        } else if (errorMessage.includes('Missing ask_id')) {
          userMessage = `❌ ${errorMessage}\n\nThis NFT needs marketplace data to be purchasable.`;
        } else if (errorMessage.includes('contract_code":"Deprecated"')) {
          userMessage = `❌ Contract appears deprecated. Please check if this is the correct contract address.`;
        } else if (errorMessage.includes('script_rejected')) {
          userMessage = `❌ Script rejected by contract. This usually means invalid parameters or the NFT is not available for purchase.`;
        } else if (errorMessage.includes('Unsupported marketplace')) {
          userMessage = `❌ ${errorMessage}\n\nPlease ensure the NFT has proper marketplace information.`;
        }
      }
      
      alert(userMessage);
      throw error;
    }
  };

  const purchaseBatch = async (nfts) => {
    if (!tezos || !wallet || !isConnected) {
      alert('Please connect your wallet first');
      return;
    }

    try {
      const totalPrice = nfts.reduce((sum, nft) => sum + nft.priceValue, 0);
      console.log(`🔍 BATCH MODE: Attempting batch purchase of ${nfts.length} NFTs for ${totalPrice} tez`);

      // Group NFTs by marketplace for batch processing
      const henTeiaNFTs = nfts.filter(nft => (nft.marketplace === 'hen' || nft.marketplace === 'teia') && nft.swapId);
      const objktNFTs = nfts.filter(nft => nft.marketplace === 'objkt' && nft.askId);
      
      console.log(`🔍 HEN/Teia NFTs: ${henTeiaNFTs.length}, OBJKT NFTs: ${objktNFTs.length}`);

      // Process HEN/Teia NFTs in batch
      if (henTeiaNFTs.length > 0) {
        const teiaContract = await tezos.wallet.at('KT1PHubm9HtyQEJ4BBpMTVomq6mhbfNZ9z5w');
        const teiaOperations = henTeiaNFTs.map(nft => 
          teiaContract.methods.collect(parseInt(nft.swapId)).toTransferParams()
        );
        
        const teiaBatch = tezos.wallet.batch(teiaOperations);
        const teiaOperation = await teiaBatch.send({ amount: henTeiaNFTs.reduce((sum, nft) => sum + nft.priceValue, 0) });
        
        console.log('🔍 HEN/Teia batch sent:', teiaOperation.opHash);
        await teiaOperation.confirmation();
      }

      // Process OBJKT NFTs in batch
      if (objktNFTs.length > 0) {
        const objktContract = await tezos.wallet.at('KT1WvzYHCNBvDSdwafTHv7nJ1dWmZ8GCYuuC');
        const objktOperations = objktNFTs.map(nft => 
          objktContract.methods.fulfill_ask(parseInt(nft.askId), null).toTransferParams()
        );
        
        const objktBatch = tezos.wallet.batch(objktOperations);
        const objktOperation = await objktBatch.send({ amount: objktNFTs.reduce((sum, nft) => sum + nft.priceValue, 0) });
        
        console.log('🔍 OBJKT batch sent:', objktOperation.opHash);
        await objktOperation.confirmation();
      }

      alert(`✅ Successfully purchased ${nfts.length} NFTs in batches!`);
      
      await updateBalance(userAddress);
      
      return 'batch_completed';
    } catch (error) {
      console.error('❌ Error purchasing batch:', error);
      const errorMessage = error?.message || error?.toString() || 'Unknown error occurred';
      alert(`Failed to purchase batch: ${errorMessage}`);
      throw error;
    }
  };

  const value = {
    wallet,
    tezos,
    userAddress,
    isConnected,
    isConnecting,
    balance,
    connectWallet,
    disconnectWallet,
    purchaseNFT,
    purchaseBatch,
    updateBalance,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

